import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Smartphone,
  Rocket,
  Layers,
  Code2,
  Cpu,
  Sparkles,
  ChevronDown,
  ClipboardList,
  ShoppingCart,
  Database,
  Globe,
  Lock,
  FolderGit2,
  Wand2,
  Terminal,
  Cloud,
  PhoneCall,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

const SYLLABUS = [
  {
    topic: "Introduction to React Native & JavaScript",
    desc: "Learn fundamentals of React Native and JavaScript for mobile app development.",
    icon: Terminal,
  },
  {
    topic: "Components, Layouts & Navigation",
    desc: "Create reusable components, layout screens, and implement navigation.",
    icon: Layers,
  },
  {
    topic: "State Management (Redux, Context API)",
    desc: "Manage app state efficiently using Redux or Context API.",
    icon: Cpu,
  },
  {
    topic: "Networking & REST API Integration",
    desc: "Fetch and display data from APIs in your React Native apps.",
    icon: Globe,
  },
  {
    topic: "Firebase Authentication & Firestore",
    desc: "Implement user authentication and store data in Firestore.",
    icon: Lock,
  },
  {
    topic: "Animations & Custom UI Components",
    desc: "Add animations and custom UI elements to enhance UX.",
    icon: Wand2,
  },
  {
    topic: "Local Storage (AsyncStorage, SQLite)",
    desc: "Save app data locally using AsyncStorage or SQLite.",
    icon: Database,
  },
  {
    topic: "Deploying to Android & iOS",
    desc: "Learn to deploy apps to Google Play and Apple App Store.",
    icon: Smartphone,
  },
];

const LABS = [
  {
    title: "Portfolio App",
    desc: "Build a personal portfolio app showcasing your projects.",
    icon: FolderGit2,
  },
  {
    title: "Chat App",
    desc: "Implement Firebase-based chat with real-time messaging.",
    icon: Users,
  },
  {
    title: "ToDo App",
    desc: "Practice state management with local storage and tasks.",
    icon: ClipboardList,
  },
  {
    title: "E-Commerce Demo",
    desc: "Create product listing, cart, and checkout flow.",
    icon: ShoppingCart,
  },
];

const SKILLS = [
  {
    text: "React Native Components & UI",
    desc: "Learn to build beautiful mobile interfaces.",
    icon: Smartphone,
  },
  {
    text: "JavaScript Programming",
    desc: "Master the programming language behind React Native.",
    icon: Code2,
  },
  {
    text: "State Management",
    desc: "Understand Redux and Context API for app state handling.",
    icon: Cpu,
  },
  {
    text: "Firebase & APIs",
    desc: "Integrate Firebase services and external APIs into your apps.",
    icon: Cloud,
  },
  {
    text: "App Deployment",
    desc: "Publish apps to both Android and iOS platforms.",
    icon: Rocket,
  },
];

const CAREERS = [
  {
    role: "React Native Developer",
    desc: "Build mobile apps for startups and companies.",
    icon: Smartphone,
  },
  {
    role: "Mobile App Engineer",
    desc: "Develop scalable and performant mobile solutions.",
    icon: Rocket,
  },
  {
    role: "Cross-Platform Developer",
    desc: "Create apps that work on multiple platforms.",
    icon: Layers,
  },
  {
    role: "UI/UX Mobile Specialist",
    desc: "Design beautiful and functional mobile interfaces.",
    icon: Sparkles,
  },
  {
    role: "Freelance App Creator",
    desc: "Work independently to deliver apps for clients.",
    icon: PhoneCall,
  },
];

const FAQS = [
  {
    q: "Do I need prior experience?",
    a: "Basic programming helps, but we start from scratch with React Native and JavaScript.",
  },
  {
    q: "Will I build real apps?",
    a: "Yes! Labs and projects include chat, e-commerce, and portfolio apps.",
  },
  {
    q: "Can I publish apps?",
    a: "Absolutely — you’ll learn how to deploy apps to both Android & iOS stores.",
  },
];

export default function ReactNative() {
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
    window.scrollTo(0, 0);
  }, []);

  // Function to render a card with icon in the center and description below
  const renderCard = (icon, title, desc, animation) => (
    <div
      className="relative p-6 bg-white rounded-2xl shadow hover:shadow-lg hover:scale-[1.02] transition flex flex-col items-center text-center"
      data-aos={animation}
    >
      <div className="w-16 h-16 flex items-center justify-center bg-blue-500 text-white rounded-full mb-4">
        {icon}
      </div>
      <h3 className="font-semibold text-indigo-600 mb-2">{title}</h3>
      <p className="text-sm text-slate-700">{desc}</p>
    </div>
  );

  const animations = [
    "fade-up",
    "fade-down",
    "flip-left",
    "flip-right",
    "zoom-in",
    "zoom-in-up",
    "fade-up-right",
    "fade-up-left",
  ];

  const getAnimation = (index) => animations[index % animations.length];

  return (
    <div className="bg-gradient-to-b from-indigo-50 via-white to-indigo-100 font-sans">
      {/* HERO */}
      <header
        className="relative z-10 py-16 text-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10">
          <h1
            data-aos="fade-down"
            className="text-4xl md:text-5xl font-bold text-blue-400 mt-5"
          >
            React Native Development
          </h1>
          <p data-aos="fade-up" className="text-gray-200 text-lg mt-3">
            Learn to build cross-platform mobile apps using React Native and
            JavaScript 💻
          </p>

          <Link to="/contact">
            <button
              data-aos="zoom-in"
              className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-full font-bold shadow-md hover:bg-blue-600 transition animate-bounce"
            >
              Enroll Now
            </button>
          </Link>
        </div>
      </header>

      {/* SYLLABUS */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            data-aos="fade-down"
          >
            Course Syllabus
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SYLLABUS.map((s, i) =>
              renderCard(
                <s.icon className="h-6 w-6 animate-bounce" />,
                s.topic,
                s.desc,
                getAnimation(i)
              )
            )}
          </div>
        </div>
      </section>

      {/* LABS */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            data-aos="fade-down"
          >
            Hands-on Labs & Projects
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LABS.map((lab, i) =>
              renderCard(
                <lab.icon className="h-6 w-6" />,
                lab.title,
                lab.desc,
                getAnimation(i)
              )
            )}
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            data-aos="fade-down"
          >
            Skills You'll Gain
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SKILLS.map((s, i) =>
              renderCard(
                <s.icon className="h-6 w-6" />,
                s.text,
                s.desc,
                getAnimation(i)
              )
            )}
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            data-aos="fade-down"
          >
            Career Opportunities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAREERS.map((c, i) =>
              renderCard(
                <c.icon className="h-6 w-6" />,
                c.role,
                c.desc,
                getAnimation(i)
              )
            )}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            data-aos="fade-down"
          >
            Learning Roadmap
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Week 1-2", desc: "JS Basics & React Native Setup" },
              { title: "Week 3-4", desc: "Components & Navigation" },
              { title: "Week 5-6", desc: "State Management & APIs" },
              { title: "Week 7-8", desc: "Firebase, Deployment & Portfolio" },
            ].map((m, i) =>
              renderCard(
                <Terminal className="h-6 w-6" />,
                m.title,
                m.desc,
                getAnimation(i)
              )
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-black">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details
                key={i}
                open={i === 0}
                className="group rounded-lg border px-4 py-3 bg-white shadow-sm transition hover:shadow-lg hover:scale-[1.02]"
                onToggle={() => setOpenFAQ(i)}
              >
                <summary className="flex items-center justify-between cursor-pointer font-medium text-black">
                  <span>{f.q}</span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </summary>
                <div className="mt-2 text-sm text-slate-700">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <footer className="py-16 px-6 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-6xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Build Apps?
          </h2>
          <p className="mb-6">
            Join the React Native course and launch your cross-platform apps
            with confidence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              className="px-6 py-3 rounded-full bg-white text-indigo-600 font-bold shadow hover:opacity-90 transition"
              to="/contact"
            >
              Talk to Counsellor
            </Link>
            <Link
              className="px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition"
              to="/contact"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
